/**
 * EmptyEventsFallback Component
 * 
 * Delightful fallback card shown when users have no upcoming events.
 * Provides clear next actions and maintains ORO's playful personality.
 */

import Button from './Button';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';

interface EmptyEventsFallbackProps {
  variant?: 'be-the-first' | 'start-your-story' | 'warming-up' | 'race-finder';
}

export function EmptyEventsFallback({ 
  variant = 'be-the-first' 
}: EmptyEventsFallbackProps) {
  const navigate = useNavigate();
  
  const variants = {
    'be-the-first': {
      illustration: '/illustrations/tying-shoes.png',
      title: 'Be the First to Move',
      description: 'No upcoming events — yet. Be the first to RSVP when clubs post new runs.',
      ctaText: 'Explore Clubs',
      ctaAction: () => navigate('/clubs'),
      secondaryCtaText: 'Browse Events',
      secondaryCtaAction: () => navigate('/events')
    },
    'start-your-story': {
      illustration: '/illustrations/start-line-shoes.png',
      title: 'No Events Yet — Start Your Story',
      description: 'Your next adventure starts here. Join a club to see upcoming events.',
      ctaText: 'Browse Clubs',
      ctaAction: () => navigate('/clubs'),
      secondaryCtaText: undefined,
      secondaryCtaAction: undefined
    },
    'warming-up': {
      illustration: '/illustrations/runners-warming-up-blur.png',
      title: 'Clubs Near You Are Warming Up',
      description: 'Local running clubs are active — join one to see their upcoming events.',
      ctaText: 'Find Clubs',
      ctaAction: () => navigate('/clubs'),
      secondaryCtaText: 'Start a Club',
      secondaryCtaAction: () => navigate('/clubs/create')
    },
    'race-finder': {
      illustration: '/illustrations/empty-trail-marker.png',
      title: 'Race Finder Coming Soon',
      description: 'Discover races and events near you — coming in a future update.',
      ctaText: 'Browse Clubs',
      ctaAction: () => navigate('/clubs'),
      secondaryCtaText: 'Explore Events',
      secondaryCtaAction: () => navigate('/events')
    }
  };
  
  const config = variants[variant];
  
  return (
    <div className="bg-gradient-to-br from-oro-surface to-oro-surface-alt border border-oro-border rounded-xl p-8 shadow-card text-center">
      {/* Illustration or Icon Fallback */}
      <div className="mb-6 flex justify-center">
        <div className="relative">
          {/* Try to load illustration, fallback to icon */}
          <img 
            src={config.illustration}
            alt=""
            className="w-48 h-48 object-contain"
            loading="lazy"
            onError={(e) => {
              // Hide image on error and show icon fallback instead
              e.currentTarget.style.display = 'none';
              const iconFallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (iconFallback) iconFallback.style.display = 'flex';
            }}
          />
          {/* Icon fallback (hidden by default) */}
          <div className="hidden w-48 h-48 items-center justify-center">
            <div className="w-24 h-24 rounded-full bg-oro-surface-alt border-2 border-oro-border flex items-center justify-center">
              <Calendar className="w-12 h-12 text-text-muted" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Title */}
      <h3 className="text-xl font-semibold text-text-primary mb-3">
        {config.title}
      </h3>
      
      {/* Description */}
      <p className="text-text-secondary mb-6 max-w-md mx-auto leading-relaxed">
        {config.description}
      </p>
      
      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button 
          onClick={config.ctaAction}
          variant="primary"
          className="min-w-[140px]"
        >
          {config.ctaText}
        </Button>
        
        {config.secondaryCtaText && (
          <Button 
            onClick={config.secondaryCtaAction}
            variant="secondary"
            className="min-w-[140px]"
          >
            {config.secondaryCtaText}
          </Button>
        )}
      </div>
    </div>
  );
}
